import React, { createContext, useContext, useState, useEffect } from "react";

/**
 * Wizard Context for managing global wizard state
 * Handles block selection, global styling, and wizard navigation
 */

// Create the context
const WizardContext = createContext();

// Hook to use the wizard context
export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error("useWizard must be used within a WizardProvider");
  }
  return context;
};

// Default wizard state
const defaultWizardState = {
  // Navigation
  currentStep: 1, // Start with block selection instead of introduction

  // Available blocks with metadata
  availableBlocks: [
    {
      id: "hero",
      name: "A Partnership Charter",
      description: "Header section with logo and tagline",
      selected: false,
    },
    {
      id: "charter",
      name: "What Is a PC?",
      description: "Partnership charter explanation",
      selected: false,
    },
    {
      id: "charterTypes",
      name: "Who Should Develop a Charter",
      description: "Target audience information",
      selected: false,
    },
    {
      id: "threeSteps",
      name: "3 Simple Steps",
      description: "Step-by-step partnership process",
      selected: false,
    },
    {
      id: "dypTool",
      name: "The DYP Tool",
      description: "Define Your Partnership tool showcase",
      selected: false,
    },
    {
      id: "partners",
      name: "Benefits for Partners",
      description: "Benefits of partnership",
      selected: false,
    },
    {
      id: "pcProcess",
      name: "All the Critical Topics Are Covered",
      description: "Partnership charter process details",
      selected: false,
    },
    {
      id: "dypAdvantages",
      name: "DYP Advantages",
      description: "Advantages of using DYP",
      selected: false,
    },
    {
      id: "assistanceLevels",
      name: "Assistance Levels",
      description: "Different levels of partnership assistance",
      selected: false,
    },
    {
      id: "bookPromo",
      name: "The Partnership Charter Book",
      description: "Partnership charter book promotion",
      selected: false,
    },
    {
      id: "testimonials",
      name: "Two Anonymized Testimonials",
      description: "Customer testimonials and reviews",
      selected: false,
    },
    {
      id: "sliversOfAmbiguity",
      name: "Slivers of Ambiguity",
      description: "Partnership clarity insights",
      selected: false,
    },
    {
      id: "dypLicensedUser",
      name: "DYP Licensed User",
      description: "Licensed user information",
      selected: false,
    },
  ],

  // Global styling configuration
  globalStyles: {
    companyName: "",
    companyAcronym: "",
    companyLogo: "https://tpci.vercel.app/phcg_logo.png",
    contactPageUrl: "", // Contact page URL for buttons
    primaryColor: "#1C436A", // Main brand blue
    primaryColorDark: "#16365A", // Darker variant
    primaryColorLight: "#1976d2", // Lighter variant
    secondaryColor: "#f5f7fa", // Light backgrounds
    cardColor: "#E5E7EA", // Card backgrounds
    textColor: "#333333", // Dark text
    accentColor: "#CE950F", // Main accent gold
    accentColorLight: "#d5ad36", // Light accent
    backgroundColor: "#ffffff", // White background
    headingFont: "serif",
    bodyFont: "sans-serif",
  },

  // Logo upload state
  logoUpload: {
    previewUrl: null, // Base64 URL for immediate preview
    blobUrl: null, // Vercel Blob URL after upload
    uploadStatus: "idle", // 'idle' | 'uploading' | 'success' | 'error'
    uploadProgress: 0, // 0-100
    error: null, // Error message if upload fails
    filename: null, // Original filename
  },

  // Individual block configurations (will be populated from App.js)
  blockConfigs: {},

  // Generated embed code
  finalEmbedCode: "",

  // Validation state
  validation: {
    step0: true, // Introduction is always valid
    step1: false, // At least one block selected
    step2: true, // Global styles are always valid
    step3: true, // Review step is always valid
  },
};

// Wizard Provider Component
export const WizardProvider = ({ children, initialBlockConfigs = {} }) => {
  const [wizardState, setWizardState] = useState(() => {
    // Check for saved state during initialization
    const savedState = localStorage.getItem("wizardState");
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        // Force update with new block names by merging with defaults
        return {
          ...defaultWizardState,
          ...parsed,
          // Always use the current availableBlocks from defaultWizardState to get updated names
          // but preserve the selected state from saved data
          availableBlocks: defaultWizardState.availableBlocks.map(
            (defaultBlock) => {
              const savedBlock = parsed.availableBlocks?.find(
                (b) => b.id === defaultBlock.id
              );
              return {
                ...defaultBlock,
                selected: savedBlock?.selected || false,
              };
            }
          ),
          blockConfigs: {
            ...initialBlockConfigs,
            ...(parsed.blockConfigs || {}),
          },
        };
      } catch (error) {
        console.warn("Failed to parse saved wizard state:", error);
      }
    }

    return {
      ...defaultWizardState,
      blockConfigs: initialBlockConfigs,
    };
  });

  // Save state changes to localStorage
  useEffect(() => {
    localStorage.setItem(
      "wizardState",
      JSON.stringify({
        currentStep: wizardState.currentStep,
        availableBlocks: wizardState.availableBlocks,
        globalStyles: wizardState.globalStyles,
        blockConfigs: wizardState.blockConfigs,
        logoUpload: wizardState.logoUpload,
      })
    );
  }, [
    wizardState.currentStep,
    wizardState.availableBlocks,
    wizardState.globalStyles,
    wizardState.blockConfigs,
    wizardState.logoUpload,
  ]);

  // Action functions
  const actions = {
    // Navigation
    setCurrentStep: (step) => {
      setWizardState((prev) => ({ ...prev, currentStep: step }));
    },

    // Block selection
    toggleBlock: (blockId) => {
      setWizardState((prev) => ({
        ...prev,
        availableBlocks: prev.availableBlocks.map((block) =>
          block.id === blockId ? { ...block, selected: !block.selected } : block
        ),
        validation: {
          ...prev.validation,
          step1: prev.availableBlocks.some((b) =>
            b.id === blockId ? !b.selected : b.selected
          ),
        },
      }));
    },

    setBlockSelection: (blockId, selected) => {
      setWizardState((prev) => ({
        ...prev,
        availableBlocks: prev.availableBlocks.map((block) =>
          block.id === blockId ? { ...block, selected } : block
        ),
        validation: {
          ...prev.validation,
          step1: prev.availableBlocks.some((b) =>
            b.id === blockId ? selected : b.selected
          ),
        },
      }));
    },

    // Global styling
    updateGlobalStyle: (key, value) => {
      setWizardState((prev) => ({
        ...prev,
        globalStyles: {
          ...prev.globalStyles,
          [key]: value,
        },
      }));
    },

    updateGlobalStyles: (styles) => {
      setWizardState((prev) => ({
        ...prev,
        globalStyles: {
          ...prev.globalStyles,
          ...styles,
        },
      }));
    },

    // Block configurations
    updateBlockConfig: (blockId, config) => {
      setWizardState((prev) => ({
        ...prev,
        blockConfigs: {
          ...prev.blockConfigs,
          [blockId]: {
            ...prev.blockConfigs[blockId],
            ...config,
          },
        },
      }));
    },

    // Embed code
    setFinalEmbedCode: (code) => {
      setWizardState((prev) => ({ ...prev, finalEmbedCode: code }));
    },

    // Logo upload actions
    updateLogoUpload: (updates) => {
      setWizardState((prev) => ({
        ...prev,
        logoUpload: {
          ...prev.logoUpload,
          ...updates,
        },
      }));
    },

    setLogoUploadStatus: (status, progress = null) => {
      setWizardState((prev) => ({
        ...prev,
        logoUpload: {
          ...prev.logoUpload,
          uploadStatus: status,
          ...(progress !== null && { uploadProgress: progress }),
        },
      }));
    },

    setLogoUploadError: (error) => {
      setWizardState((prev) => ({
        ...prev,
        logoUpload: {
          ...prev.logoUpload,
          uploadStatus: "error",
          error: error,
        },
      }));
    },

    setLogoUploadSuccess: (blobUrl) => {
      setWizardState((prev) => ({
        ...prev,
        globalStyles: {
          ...prev.globalStyles,
          companyLogo: blobUrl,
        },
        logoUpload: {
          ...prev.logoUpload,
          blobUrl: blobUrl,
          uploadStatus: "success",
          uploadProgress: 100,
          error: null,
        },
      }));
    },

    // Reset wizard
    resetWizard: () => {
      setWizardState({
        ...defaultWizardState,
        blockConfigs: initialBlockConfigs,
      });
      localStorage.removeItem("wizardState");
    },

    // Validation
    isStepValid: (step) => {
      switch (step) {
        case 0:
          return wizardState.validation.step0;
        case 1:
          return wizardState.validation.step1;
        case 2:
          // Can't proceed if logo is uploading
          return (
            wizardState.validation.step2 &&
            wizardState.logoUpload.uploadStatus !== "uploading"
          );
        case 3:
          return wizardState.validation.step3;
        default:
          return true;
      }
    },
  };

  // Get selected blocks
  const selectedBlocks = wizardState.availableBlocks.filter(
    (block) => block.selected
  );

  const contextValue = {
    ...wizardState,
    selectedBlocks,
    ...actions,
  };

  return (
    <WizardContext.Provider value={contextValue}>
      {children}
    </WizardContext.Provider>
  );
};

export default WizardContext;
